import { useState, useEffect } from 'react'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AdminPhotographer {
  id: string
  user_id: string
  display_name: string
  email: string
  created_at: string
  total_missions: number
  total_earned: number
  status: 'active' | 'suspended' | 'pending'
  city: string | null
  verified: boolean
}

export interface AdminMission {
  id: string
  title: string
  created_by: string | null
  client_name: string
  status: 'active' | 'completed' | 'cancelled' | 'draft'
  images_delivered: number
  required_photos: number
  deadline: string | null
  created_at: string
  reward_credits: number
}

export interface PendingImage {
  id: string
  photo_url: string
  mission_id: string
  mission_title: string
  photographer_id: string
  photographer_name: string
  created_at: string
  latitude: number | null
  longitude: number | null
  approved: boolean | null
  rejection_reason: string | null
}

export interface PendingPayout {
  id: string
  user_id: string
  photographer_name: string
  amount: number
  net_amount: number
  currency: string
  created_at: string
  wallet_balance: number
  swish_number: string
  status: string
}

export interface AdminStats {
  total_photographers: number
  active_missions: number
  images_this_week: number
  revenue_mtd: number
}

export interface AdminClient {
  id: string
  organization_name: string
  contact_email: string
  contact_person: string
  plan: string
  credits_remaining: number
  active_missions: number
  invoice_status: 'paid' | 'pending' | 'overdue'
  status: string
}

export interface PhotographerFilters {
  status?: 'active' | 'suspended' | 'pending' | 'all'
  search?: string
}

export interface MissionFilters {
  status?: 'active' | 'completed' | 'cancelled' | 'draft' | 'all'
  dateFrom?: string
  dateTo?: string
}

export interface ImageFilters {
  status?: 'pending' | 'approved' | 'rejected' | 'all'
}

// ─── Mock data ───────────────────────────────────────────────────────────────

const MOCK_PHOTOGRAPHERS: AdminPhotographer[] = [
  { id: '1', user_id: 'u1', display_name: 'Anna Lindqvist', email: 'anna@example.com', created_at: '2024-01-15T10:00:00Z', total_missions: 47, total_earned: 23500, status: 'active', city: 'Stockholm', verified: true },
  { id: '2', user_id: 'u2', display_name: 'Marcus Bergström', email: 'marcus@example.com', created_at: '2024-02-03T10:00:00Z', total_missions: 12, total_earned: 6000, status: 'active', city: 'Göteborg', verified: true },
  { id: '3', user_id: 'u3', display_name: 'Sara Nilsson', email: 'sara@example.com', created_at: '2024-03-10T10:00:00Z', total_missions: 0, total_earned: 0, status: 'pending', city: 'Malmö', verified: false },
  { id: '4', user_id: 'u4', display_name: 'Jonas Eriksson', email: 'jonas@example.com', created_at: '2023-11-20T10:00:00Z', total_missions: 89, total_earned: 44500, status: 'active', city: 'Uppsala', verified: true },
  { id: '5', user_id: 'u5', display_name: 'Maja Karlsson', email: 'maja@example.com', created_at: '2024-01-28T10:00:00Z', total_missions: 3, total_earned: 1500, status: 'suspended', city: 'Linköping', verified: true },
  { id: '6', user_id: 'u6', display_name: 'Erik Svensson', email: 'erik2@example.com', created_at: '2024-03-01T10:00:00Z', total_missions: 8, total_earned: 4000, status: 'active', city: 'Västerås', verified: false },
  { id: '7', user_id: 'u7', display_name: 'Lena Pettersson', email: 'lena@example.com', created_at: '2024-02-15T10:00:00Z', total_missions: 23, total_earned: 11500, status: 'active', city: 'Örebro', verified: true },
]

const MOCK_MISSIONS: AdminMission[] = [
  { id: 'M-001', title: 'Infrastrukturkontroll Sthlm', created_by: 'c1', client_name: 'Trafikverket', status: 'active', images_delivered: 12, required_photos: 20, deadline: '2024-04-15T00:00:00Z', created_at: '2024-03-01T10:00:00Z', reward_credits: 500 },
  { id: 'M-002', title: 'Byggarbetsplats Norrmalm', created_by: 'c2', client_name: 'Skanska AB', status: 'active', images_delivered: 5, required_photos: 10, deadline: '2024-04-20T00:00:00Z', created_at: '2024-03-05T10:00:00Z', reward_credits: 250 },
  { id: 'M-003', title: 'Parkinspektion Haga', created_by: 'c3', client_name: 'Göteborgs Stad', status: 'completed', images_delivered: 15, required_photos: 15, deadline: '2024-03-20T00:00:00Z', created_at: '2024-02-20T10:00:00Z', reward_credits: 375 },
  { id: 'M-004', title: 'Vägkontroll E4', created_by: 'c1', client_name: 'Trafikverket', status: 'cancelled', images_delivered: 2, required_photos: 30, deadline: '2024-03-10T00:00:00Z', created_at: '2024-02-28T10:00:00Z', reward_credits: 750 },
  { id: 'M-005', title: 'Fastighetsinspektion Östermalm', created_by: 'c4', client_name: 'Wallenstam', status: 'draft', images_delivered: 0, required_photos: 8, deadline: '2024-05-01T00:00:00Z', created_at: '2024-03-15T10:00:00Z', reward_credits: 200 },
]

const MOCK_IMAGES: PendingImage[] = [
  { id: 'I-001', photo_url: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=400', mission_id: 'M-001', mission_title: 'Infrastrukturkontroll Sthlm', photographer_id: 'u1', photographer_name: 'Anna Lindqvist', created_at: '2024-03-20T14:30:00Z', latitude: 59.3293, longitude: 18.0686, approved: null, rejection_reason: null },
  { id: 'I-002', photo_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400', mission_id: 'M-001', mission_title: 'Infrastrukturkontroll Sthlm', photographer_id: 'u4', photographer_name: 'Jonas Eriksson', created_at: '2024-03-20T15:10:00Z', latitude: 59.3320, longitude: 18.0700, approved: null, rejection_reason: null },
  { id: 'I-003', photo_url: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=400', mission_id: 'M-002', mission_title: 'Byggarbetsplats Norrmalm', photographer_id: 'u2', photographer_name: 'Marcus Bergström', created_at: '2024-03-19T09:45:00Z', latitude: 59.3346, longitude: 18.0560, approved: true, rejection_reason: null },
  { id: 'I-004', photo_url: 'https://images.unsplash.com/photo-1517557837440-66b8e1c9aadf?w=400', mission_id: 'M-002', mission_title: 'Byggarbetsplats Norrmalm', photographer_id: 'u7', photographer_name: 'Lena Pettersson', created_at: '2024-03-19T11:20:00Z', latitude: 59.3350, longitude: 18.0580, approved: false, rejection_reason: 'Bilden är otydlig och visar inte målbyggnaden' },
  { id: 'I-005', photo_url: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=400', mission_id: 'M-001', mission_title: 'Infrastrukturkontroll Sthlm', photographer_id: 'u6', photographer_name: 'Erik Svensson', created_at: '2024-03-21T08:00:00Z', latitude: 59.3280, longitude: 18.0650, approved: null, rejection_reason: null },
]

const MOCK_PAYOUTS: PendingPayout[] = [
  { id: 'P-001', user_id: 'u1', photographer_name: 'Anna Lindqvist', amount: 5000, net_amount: 3825, currency: 'SEK', created_at: '2024-03-18T10:00:00Z', wallet_balance: 23500, swish_number: '0701234567', status: 'pending' },
  { id: 'P-002', user_id: 'u4', photographer_name: 'Jonas Eriksson', amount: 10000, net_amount: 7650, currency: 'SEK', created_at: '2024-03-17T10:00:00Z', wallet_balance: 44500, swish_number: '0709876543', status: 'pending' },
  { id: 'P-003', user_id: 'u7', photographer_name: 'Lena Pettersson', amount: 2000, net_amount: 1530, currency: 'SEK', created_at: '2024-03-16T10:00:00Z', wallet_balance: 11500, swish_number: '0705554433', status: 'pending' },
]

const MOCK_CLIENTS: AdminClient[] = [
  { id: 'C-001', organization_name: 'Trafikverket', contact_email: 'order@trafikverket.se', contact_person: 'Anna Holm', plan: 'Enterprise', credits_remaining: 45000, active_missions: 3, invoice_status: 'paid', status: 'active' },
  { id: 'C-002', organization_name: 'Skanska AB', contact_email: 'procurement@skanska.se', contact_person: 'Björn Lund', plan: 'Pro', credits_remaining: 8500, active_missions: 2, invoice_status: 'pending', status: 'active' },
  { id: 'C-003', organization_name: 'Göteborgs Stad', contact_email: 'stadsbyggnad@goteborg.se', contact_person: 'Maria Berg', plan: 'Pro', credits_remaining: 1200, active_missions: 0, invoice_status: 'paid', status: 'active' },
  { id: 'C-004', organization_name: 'Wallenstam', contact_email: 'ops@wallenstam.se', contact_person: 'Peter Ström', plan: 'Starter', credits_remaining: 0, active_missions: 1, invoice_status: 'overdue', status: 'active' },
]

// ─── Hooks ───────────────────────────────────────────────────────────────────

export function usePhotographers(filters?: PhotographerFilters) {
  const [data, setData] = useState<AdminPhotographer[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    // Simulate API delay
    const timer = setTimeout(() => {
      let result = [...MOCK_PHOTOGRAPHERS]

      if (filters?.status && filters.status !== 'all') {
        result = result.filter(p => p.status === filters.status)
      }

      if (filters?.search) {
        const q = filters.search.toLowerCase()
        result = result.filter(
          p =>
            p.display_name.toLowerCase().includes(q) ||
            p.email.toLowerCase().includes(q)
        )
      }

      setData(result)
      setIsLoading(false)
    }, 300)

    return () => clearTimeout(timer)
  }, [filters?.status, filters?.search])

  const suspend = async (userId: string) => {
    setData(prev =>
      prev.map(p => (p.user_id === userId ? { ...p, status: 'suspended' as const } : p))
    )
  }

  const activate = async (userId: string) => {
    setData(prev =>
      prev.map(p => (p.user_id === userId ? { ...p, status: 'active' as const } : p))
    )
  }

  return { data, isLoading, suspend, activate }
}

export function useMissions(filters?: MissionFilters) {
  const [data, setData] = useState<AdminMission[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    const timer = setTimeout(() => {
      let result = [...MOCK_MISSIONS]

      if (filters?.status && filters.status !== 'all') {
        result = result.filter(m => m.status === filters.status)
      }

      setData(result)
      setIsLoading(false)
    }, 300)

    return () => clearTimeout(timer)
  }, [filters?.status])

  const cancelMission = async (id: string) => {
    setData(prev =>
      prev.map(m => (m.id === id ? { ...m, status: 'cancelled' as const } : m))
    )
  }

  const completeMission = async (id: string) => {
    setData(prev =>
      prev.map(m => (m.id === id ? { ...m, status: 'completed' as const } : m))
    )
  }

  return { data, isLoading, cancelMission, completeMission }
}

export function usePendingImages(filters?: ImageFilters) {
  const [data, setData] = useState<PendingImage[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    const timer = setTimeout(() => {
      let result = [...MOCK_IMAGES]

      if (filters?.status && filters.status !== 'all') {
        if (filters.status === 'pending') result = result.filter(i => i.approved === null)
        else if (filters.status === 'approved') result = result.filter(i => i.approved === true)
        else if (filters.status === 'rejected') result = result.filter(i => i.approved === false)
      }

      setData(result)
      setIsLoading(false)
    }, 300)

    return () => clearTimeout(timer)
  }, [filters?.status])

  const approveImage = async (id: string) => {
    setData(prev => prev.map(i => (i.id === id ? { ...i, approved: true } : i)))
  }

  const rejectImage = async (id: string, reason: string) => {
    setData(prev =>
      prev.map(i => (i.id === id ? { ...i, approved: false, rejection_reason: reason } : i))
    )
  }

  return { data, isLoading, approveImage, rejectImage }
}

export function usePendingPayouts() {
  const [data, setData] = useState<PendingPayout[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    const timer = setTimeout(() => {
      setData(MOCK_PAYOUTS.filter(p => p.status === 'pending'))
      setIsLoading(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [])

  const approvePayout = async (id: string) => {
    setData(prev => prev.filter(p => p.id !== id))
  }

  const rejectPayout = async (id: string) => {
    setData(prev => prev.filter(p => p.id !== id))
  }

  return { data, isLoading, approvePayout, rejectPayout }
}

export function useAdminStats() {
  const [data, setData] = useState<AdminStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setData({
        total_photographers: 247,
        active_missions: 18,
        images_this_week: 1340,
        revenue_mtd: 284500,
      })
      setIsLoading(false)
    }, 400)
    return () => clearTimeout(timer)
  }, [])

  return { data, isLoading }
}

export function useAdminClients() {
  const [data] = useState<AdminClient[]>(MOCK_CLIENTS)
  const [isLoading] = useState(false)

  const addCredits = (id: string, amount: number) => {
    console.log('Add credits', id, amount)
  }

  return { data, isLoading, addCredits }
}
